import React, { useState } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarDays, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { WhatsAppIcon, waUrl } from "./WhatsAppButton";
import { hotel } from "../mock";

const fmt = (d) => (d ? format(d, "d MMMM yyyy", { locale: tr }) : null);

const DateField = ({ label, value, placeholder, onChange, disabled }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="w-full text-left px-6 py-5 hover:bg-white/[0.06] transition-colors"
      >
        <span className="block text-[10px] tracking-[0.24em] uppercase text-[#e7cf9c] mb-2">
          {label}
        </span>
        <span className="flex items-center gap-2.5 text-[16px] text-white font-400">
          <CalendarDays size={17} className="text-[#d9b877]" />
          {value ? fmt(value) : <span className="text-white/50">{placeholder}</span>}
        </span>
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={value} onSelect={onChange} disabled={disabled} locale={tr} weekStartsOn={1} initialFocus />
    </PopoverContent>
  </Popover>
);

const QuickReservationBar = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState("2");

  const nights =
    checkIn && checkOut ? Math.max(differenceInCalendarDays(checkOut, checkIn), 0) : 0;

  const handleReserve = () => {
    if (!checkIn || !checkOut) {
      toast.error("Lütfen giriş ve çıkış tarihlerini seçin.");
      return;
    }
    if (nights < 1) {
      toast.error("Çıkış tarihi girişten sonra olmalıdır.");
      return;
    }
    const msg =
      `${hotel.whatsappIntro}\n` +
      `Giriş: ${fmt(checkIn)}\n` +
      `Çıkış: ${fmt(checkOut)}\n` +
      `Misafir: ${guests} yetişkin\n` +
      `Konaklama: ${nights} gece\n` +
      `Müsaitlik ve fiyat bilgisi rica ediyorum.`;
    window.open(waUrl(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="glass-dark shadow-[0_30px_70px_-34px_rgba(0,0,0,0.75)]">
        <div className="flex flex-col md:flex-row md:items-stretch divide-y md:divide-y-0 md:divide-x divide-white/12">
          <div className="md:flex-1">
            <DateField
              label="Giriş Tarihi"
              placeholder="Tarih seçin"
              value={checkIn}
              onChange={(d) => {
                setCheckIn(d);
                if (checkOut && d && checkOut <= d) setCheckOut(null);
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </div>
          <div className="md:flex-1">
            <DateField
              label="Çıkış Tarihi"
              placeholder="Tarih seçin"
              value={checkOut}
              onChange={setCheckOut}
              disabled={(d) => d <= (checkIn || new Date(new Date().setHours(0, 0, 0, 0)))}
            />
          </div>
          <div className="md:flex-1">
            <div className="px-6 py-5">
              <span className="block text-[10px] tracking-[0.24em] uppercase text-[#e7cf9c] mb-2">
                Misafir Sayısı
              </span>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger className="!bg-transparent !border-0 !shadow-none !p-0 !h-auto text-[16px] font-400 text-white focus:ring-0 [&>span]:flex [&>span]:items-center [&>span]:gap-2.5 [&>svg]:text-white/60">
                  <span className="flex items-center gap-2.5">
                    <Users size={17} className="text-[#d9b877]" />
                    <SelectValue />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {["1", "2", "3", "4", "5", "6"].map((g) => (
                    <SelectItem key={g} value={g}>
                      {g} Yetişkin
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <button
            onClick={handleReserve}
            data-testid="quick-reserve"
            className="btn-gold flex items-center justify-center gap-3 px-8 py-6 md:py-0 md:min-w-[224px]"
          >
            <WhatsAppIcon size={18} />
            Rezervasyon Yap
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
      <p className="text-center md:text-left text-[12px] text-[#6b6459] md:text-white/75 mt-3.5 px-1">
        WhatsApp üzerinden hızlı rezervasyon — seçimleriniz otomatik iletilir. Bu bir
        <span className="text-[#9a7a3f] md:text-[#e7cf9c]"> talep</span>tir; müsaitlik ve fiyat teyidi sonrası kesinleşir.
      </p>
    </div>
  );
};

export default QuickReservationBar;
